"use client"

import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, Maximize2, Minimize2, Copy, CheckCircle, FileText, Loader2, BrainCircuit, Sparkles, Target, TrendingUp, Paperclip, MessageSquare, User, Download, Eye, Send, File, FileImage, Mail, ChevronDown, ListChecks } from "lucide-react";
import { Button, Input, Separator, Textarea, useToast } from "@mantis-sas/ui";
import { updateExtractedField } from "../../../../ApiCalls/mantisFlowsManangment";
import { sendGenericEmail } from "../../../../ApiCalls/mantisEmail";


const mockAttachments = [
  { id: 1, name: "Documento_Soporte_Final.pdf", size: "1.1 MB", date: "2024-07-27", type: "pdf", url: "#" },
  { id: 2, name: "Grafico_Analisis_Q3.png", size: "350 KB", date: "2024-07-26", type: "image", url: "#" },
  { id: 3, name: "Datos_Adicionales.xlsx", size: "85 KB", date: "2024-07-26", type: "excel", url: "#" }
];

// Helper to get attachment icon
const getAttachmentIcon = (type) => {
  // Normalize type to lowercase
  const lowerType = type?.toLowerCase();
  switch (lowerType) {
    case "pdf": return <FileText className="h-5 w-5 text-red-500" />;
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "bmp":
       return <FileImage className="h-5 w-5 text-blue-500" />;
    case "xls":
    case "xlsx":
       return <FileText className="h-5 w-5 text-green-500" />;
    case "eml":
    case "email":
       return <Mail className="h-5 w-5 text-purple-500" />;
    case "correo_content": // <-- Add case for the new correo content type
       return <MessageSquare className="h-5 w-5 text-blue-500" />;
    default: return <File className="h-5 w-5 text-slate-500" />;
  }
};

// Tab component
const Tab = ({ tabs, activeTab, onTabChange }) => (
  <div className="flex space-x-2 border-b border-slate-200 dark:border-slate-700 mb-5">
    {tabs.map((tab, index) => (
      <button
        key={index}
        onClick={() => onTabChange(tab)}
        className={`px-4 py-2.5 text-sm font-medium transition-all duration-200 border-b-2 mb-[-1px]
          ${
            activeTab === tab
              ? "border-blue-500 text-blue-600 dark:text-blue-400"
              : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:border-slate-300 dark:hover:border-slate-600"
          }
        `}
      >
        {tab}
      </button>
    ))}
  </div>
);

// Función para formatear las acciones sugeridas
const formatAccionesSugeridas = (texto) => {
  if (!texto) return "";
  
  // Reemplazar los patrones numerados (1., 2., etc.) con saltos de línea y espaciado
  return texto
    .replace(/(\d+\.)\s*/g, '\n$1 ') // Añade salto de línea antes de cada número
    .trim() // Elimina espacios al inicio y final
    .split('\n') // Divide por líneas
    .filter(line => line.trim()) // Elimina líneas vacías
    .map(line => line.trim()) // Elimina espacios al inicio y final de cada línea
    .join('\n\n'); // Une con doble salto de línea para mejor espaciado
};

export default function FlowModalETB({
  data,
  onClose,
  columnLabels = {},
  formatCellValue,
  clientId,
  workflowId,
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchText] = useState("");
  const [, setCopied] = useState(false);
  const [isRetrainingMode, setIsRetrainingMode] = useState(false);
  const [retrainingReason, setRetrainingReason] = useState("");
  const [isRetraining, setIsRetraining] = useState(false);
  const [retrainingComplete, setRetrainingComplete] = useState(false);
  const [accuracyBefore, setAccuracyBefore] = useState(0);
  const [accuracyAfter, setAccuracyAfter] = useState(0);
  const [retrainingProgress, setRetrainingProgress] = useState(0);
  const [retrainingStage, setRetrainingStage] = useState("");
  const [editedData, setEditedData] = useState({});
  const [hasEdits, setHasEdits] = useState(false);
  const [activeTab, setActiveTab] = useState("Detalles Extraídos");
  const retrainingStages = [
    "Analizando datos de entrenamiento...",
    "Preparando vectores de entrada...",
    "Ajustando parámetros del modelo...",
    "Optimizando capas neuronales...",
    "Validando resultados...",
    "Finalizando reentrenamiento..."
  ];
  const [selectedFileToPreview, setSelectedFileToPreview] = useState(null);
  const { toast } = useToast();
  const textareaRef = useRef(null);
  const commentTextareaRef = useRef(null);
  const [newComment, setNewComment] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [, setSelectedGroup] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [isReassigning, setIsReassigning] = useState(false);
  const [isSendingResponse, setIsSendingResponse] = useState(false);
  const [attachmentsToSend, setAttachmentsToSend] = useState([]);
  const fileInputRef = useRef(null);
  const [recipientEmail, setRecipientEmail] = useState("");

  // Define comments state
  const [comments, setComments] = useState(() => {
    try {
      const commentsData = data?.comentarios;
      if (typeof commentsData === 'string') {
        const parsedData = JSON.parse(commentsData);
        return Array.isArray(parsedData) ? parsedData : [parsedData];
      } else if (typeof commentsData === 'object' && commentsData !== null) {
        return Array.isArray(commentsData) ? commentsData : [commentsData];
      }
    } catch (error) {
      console.error("Error parsing comentarios JSON:", error);
    }
    return [];
  });

  const filesToPreview = Object.entries(data)
    .filter(([key, value]) => value && key.toLowerCase().includes("url"))
    .map(([key, value]) => {
      let name = key.toLowerCase().replace("file_url_", "").replace(/_/g, " ");
      name = name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      const extension = typeof value === 'string' ? value.split('.').pop() : 'unknown';
      return {
        name: name,
        url: value,
        type: extension,
      };
    });

  // Check if 'correo' data exists and add a specific entry for it
  if (data?.correo && typeof data.correo === 'string' && data.correo.trim() !== "") {
      // Add it as a previewable item
      filesToPreview.push({
          name: "Detalle Correo", // Or "Contenido Correo", "Asunto", etc.
          url: null, // No direct URL
          type: "correo_content", // Special type for this content
          content: data.correo // Store the content directly if needed later, or rely on accessing data.correo
      });
  }

  // Check if email data exists (sender, subject, body) and add a specific entry for it
  // (Keep the previous logic for sender/subject/body if needed, or remove if 'correo' replaces it)
  if (data?.senderEmail || data?.subject || data?.body) {
      const hasEmailUrl = filesToPreview.some(file => file.url?.toLowerCase().endsWith('.eml'));
      // Avoid adding if 'correo_content' already exists OR if an eml file exists
      const hasCorreoContent = filesToPreview.some(file => file.type === 'correo_content');
      if (!hasEmailUrl && !hasCorreoContent) {
          filesToPreview.push({
              name: "Correo Electrónico",
              url: null,
              type: "email"
          });
      } else if (hasEmailUrl) {
         filesToPreview.forEach(file => {
             if (file.url?.toLowerCase().endsWith('.eml')) {
                 file.type = 'email';
                 if (!file.name || file.name.toLowerCase().includes('url')) {
                     file.name = "Correo Electrónico";
                 }
             }
         });
      }
  }

  // Set initial selected file (important: check if filesToPreview has items)
  useEffect(() => {
    if (filesToPreview.length > 0 && !selectedFileToPreview) {
      // Prioritize showing 'correo_content' if it exists
      const correoContentFile = filesToPreview.find(f => f.type === 'correo_content');
      setSelectedFileToPreview(correoContentFile || filesToPreview[0]);
    } else if (filesToPreview.length === 0) {
        setSelectedFileToPreview(null); // Ensure nothing is selected if no files
    }
  }, [filesToPreview, selectedFileToPreview]); // Keep dependencies

  useEffect(() => {
    if (isRetrainingMode && !isRetraining && !retrainingComplete && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [retrainingReason, isRetrainingMode, isRetraining, retrainingComplete]);

  useEffect(() => {
    if (commentTextareaRef.current) {
      commentTextareaRef.current.style.height = "auto";
      const scrollHeight = commentTextareaRef.current.scrollHeight;
      const maxHeight = 150;
      commentTextareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [newComment]);

  useEffect(() => {
    if (data?.email_remitente) {
      setRecipientEmail(data.email_remitente);
    }
  }, [data?.email_remitente]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
      const toggleRetrainingMode = () => {
    const enteringRetraining = !isRetrainingMode;
    setIsRetrainingMode(enteringRetraining);
    if (enteringRetraining) {
      const initialEditedData = {};
      Object.entries(data)
        .filter(([key]) => key !== "id" && !key.toLowerCase().includes("url"))
        .forEach(([key, value]) => {
          initialEditedData[key] = value;
        });
      setEditedData(initialEditedData);

      setRetrainingReason("");
      setRetrainingComplete(false);
      setIsRetraining(false);
      setRetrainingProgress(0);
      setRetrainingStage("");
      setHasEdits(false);
    }
  };

  const handleFieldEdit = (key, value) => {
    setEditedData(prev => ({ ...prev, [key]: value }));
    setHasEdits(true);
  };

  const startRetraining = () => {
    if (!hasEdits && !retrainingReason) {
      if (!hasEdits) {
        toast({
          title: "Sin cambios",
          description: "Realice al menos una corrección en los datos para iniciar el reentrenamiento.",
          variant: "warning",
          duration: 3000,
        });
        return;
      }
    }

    let reason = retrainingReason;
    if (!reason && hasEdits) {
      const changedFields = Object.entries(editedData)
        .filter(([key, value]) => data[key] !== value)
        .map(([key]) => getColumnLabel(key))
        .join(", ");
      reason = `Corrección de datos en los campos: ${changedFields}`;
      setRetrainingReason(reason);
    }

    setIsRetraining(true);
    setRetrainingProgress(0);
    setRetrainingStage(retrainingStages[0]);

    let stageIndex = 0;
    let progressValue = 0;
    const progressInterval = setInterval(() => {
      progressValue += 2;

      if (progressValue >= 100) {
        clearInterval(progressInterval);
      } else {
        const currentStageIndex = Math.min(Math.floor(progressValue / 20), retrainingStages.length - 1);
        if (currentStageIndex > stageIndex) {
          stageIndex = currentStageIndex;
          setRetrainingStage(retrainingStages[stageIndex]);
        }
        setRetrainingProgress(progressValue);
      }
    }, 60);

    setTimeout(() => {
      clearInterval(progressInterval);

      const previousAccuracy = 78.5;
      const newAccuracy = Math.min(99.9, previousAccuracy + Math.random() * (99.9 - previousAccuracy));

      setRetrainingProgress(100);
      setRetrainingStage(retrainingStages[retrainingStages.length - 1]);

      setTimeout(() => {
        setAccuracyBefore(previousAccuracy);
        setAccuracyAfter(newAccuracy);
        setIsRetraining(false);
        setRetrainingComplete(true);

        toast({
          title: "✨ Reentrenamiento Completado",
          description: `La precisión ha mejorado de ${previousAccuracy.toFixed(1)}% a ${newAccuracy.toFixed(1)}%`,
          duration: 5000,
        });

        const updatedData = { ...data };
        Object.entries(editedData).forEach(([key, value]) => {
          updatedData[key] = value;
        });
        Object.assign(data, updatedData);

        setHasEdits(false);

      }, 500);
    }, 3000);
  };

  const copyToClipboard = (textToCopy) => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      toast({
        title: "Copiado al portapapeles",
        duration: 2000,
      });
      setTimeout(() => setCopied(false), 2000);
    }, (err) => {
      toast({
        title: "Error al copiar",
        description: "No se pudo copiar al portapapeles.",
        variant: "destructive",
        duration: 3000,
      });
      console.error('Could not copy text: ', err);
    });
  };

  
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || typeof text !== "string") return text;

    const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200 dark:bg-yellow-700 px-0.5 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const getColumnLabel = (column) => {
    return columnLabels[column] || column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatValueForDisplay = (value) => {
    if (typeof value === "boolean") {
      return value ? "Sí" : "No";
    }
    if (value === null || typeof value === 'undefined') {
      return "-";
    }
    // Add check for objects and arrays before default string conversion
    if (typeof value === 'object' && value !== null) {
      try {
        // Use JSON.stringify with indentation for readability
        return JSON.stringify(value, null, 2);
      } catch (e) {
        // Fallback in case of circular references or other errors
        return "[Objeto complejo]";
      }
    }
    // Use the custom formatter if provided, otherwise convert to string
    return formatCellValue ? formatCellValue(value) : String(value);
  };

  const handleSaveChanges = async () => {
    // Verificar si hay cambios para guardar
    if (Object.keys(editedData).length === 0) {
      toast({
        title: "Sin cambios",
        description: "No hay cambios para guardar",
        duration: 3000,
      });
      return;
    }

    try {
      // Guardar cada campo modificado
      for (const [key, value] of Object.entries(editedData)) {
        if (data[key] !== value) {
          const rowId = data?.id;
          const runId = data?.workflow_run_id || "1";
          await updateExtractedField(clientId, workflowId, key, value, rowId, runId);
        }
      }

      // Actualizar los datos originales con los cambios
      Object.entries(editedData).forEach(([key, value]) => {
        data[key] = value;
      });

      // Limpiar el estado de edición
      setEditedData({});
      setIsEditing(false);

      toast({
        title: "Cambios guardados",
        description: "Los cambios se han guardado correctamente",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error al guardar los cambios:", error);
      toast({
        title: "Error",
        description: "No se pudieron guardar los cambios",
        duration: 3000,
      });
    }
  };
  
  const handleUpdateField = async (field, value) => {
    // Si se llama sin argumentos (desde el botón de comentario), usar valores por defecto
    const targetField = field || "comentarios";
    const targetValue = value !== undefined ? value : newComment; // Usar newComment si value no se pasó

    if (targetField === "comentarios" && !targetValue.trim()) {
      toast({
        title: "Comentario vacío",
        description: "Escribe algo antes de guardar.",
        variant: "warning",
        duration: 2000,
      });
      return;
    }

    const rowId = data?.id;
    const runId = data?.workflow_run_id || "1";

    if (!clientId || !workflowId || !rowId) {
      console.error("Faltan datos esenciales (clientId, workflowId, rowId) para guardar.");
      toast({
        title: "Error",
        description: "No se pueden obtener los identificadores necesarios.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    let valueToSave = targetValue;
    let updatedCommentsArrayForState = null; // Para actualizar el estado local si es un comentario

    // --- Lógica específica para guardar comentarios ---
    if (targetField === "comentarios") {
      const commentObject = {
        id: Date.now(), // ID temporal para la key en React
        // Usar nombre de usuario real si está disponible, si no, un placeholder.
        // Asegúrate de que 'currentUser' esté disponible en este scope.
        clientName:"Usuario", // Eliminado "Usuario Actual"
        message: targetValue, // El texto del nuevo comentario
        date: new Date().toISOString(), // Fecha actual
      };

      // Obtener comentarios actuales del estado, asegurando que sea un array
      const currentComments = Array.isArray(comments) ? comments : [];
      const updatedComments = [...currentComments, commentObject];

      // Guardar el array completo como string JSON
      valueToSave = JSON.stringify(updatedComments);
      updatedCommentsArrayForState = updatedComments; // Guardar el array para el estado local
    }
    // --- Fin lógica específica de comentarios ---

    // Llamar a la API para guardar en el backend
    try {
      console.log(`Updating field: ${targetField} for rowId: ${rowId}`);
      await updateExtractedField(clientId, workflowId, targetField, valueToSave, rowId, runId);

      // Actualizar estado local DESPUÉS de guardar exitosamente
      if (targetField === "comentarios" && updatedCommentsArrayForState) {
        setComments(updatedCommentsArrayForState); // Actualizar estado de comentarios
        setNewComment(""); // Limpiar input
      } else if (targetField !== "comentarios") {
        // Actualizar otros campos en el estado local si es necesario
        // (Ej: si se edita un campo directamente en la tabla de detalles)
        if (data) data[targetField] = targetValue; // Actualiza prop (con precaución)
        if (editedData && targetField in editedData) {
           setEditedData(prev => ({ ...prev, [targetField]: targetValue }));
        }
      }

      toast({
        title: "Actualización exitosa",
        description: targetField === "comentarios" ? "El comentario ha sido guardado." : `El campo ${getColumnLabel(targetField)} ha sido actualizado.`,
        duration: 3000,
      });

    } catch (error) {
      console.error(`Error al guardar el campo ${targetField}:`, error);
      toast({
        title: "Error",
        description: `No se pudo guardar ${targetField === "comentarios" ? "el comentario" : "el campo"}.`,
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const handleReassign = async (groupId, groupName) => {
    // Declarar rowId y runId al inicio de la función
    const rowId = data?.id;
    const runId = data?.workflow_run_id || "1";

    // Verificar si tenemos los IDs necesarios
    if (!rowId || !clientId || !workflowId) {
      console.error("Faltan datos esenciales (rowId, clientId, workflowId) para reasignar.");
      toast({
        title: "Error",
        description: "No se pueden obtener los identificadores necesarios para la reasignación.",
        variant: "destructive",
        duration: 3000,
      });
      return; // Salir si faltan datos
    }

    setIsReassigning(true);

    try {
      // Actualizar el campo de grupo en editedData
      setEditedData(prev => ({
        ...prev,
        group_id: groupId,
        grupo_responsable: groupName
      }));

      setSelectedGroup(groupName);

      // Realizar la primera consulta para actualizar group_id
      await updateExtractedField(clientId, workflowId, "group_id", groupId, rowId, runId);

      // Realizar la segunda consulta para actualizar grupo_responsable
      await updateExtractedField(clientId, workflowId, "grupo_responsable", groupName, rowId, runId);

      // Actualizar los datos originales con los cambios
      if (data) { // Asegurarse que data existe
        data.group_id = groupId;
        data.grupo_responsable = groupName;
      }

      toast({
        title: "Grupo reasignado",
        description: `El caso ha sido reasignado a ${groupName}`,
        duration: 3000,
      });

      setShowReassignModal(false);
    } catch (error) {
      console.error("Error al reasignar el grupo:", error);
      toast({
        title: "Error",
        description: "No se pudo reasignar el grupo",
        variant: "destructive", // Añadir variante destructiva para errores
        duration: 3000,
      });
    } finally {
      setIsReassigning(false);
    }
  };

  // Definir los grupos disponibles
  const availableGroups = [
    { id: "67f6eb642584a8635b992d0c", name: "Soporte Técnico" },
    { id: "67f6eb702584a8635b992d15", name: "Facturación" },
    { id: "67f6eb902584a8635b992d30", name: "Atención al Cliente" } 
  ];

  const tabs = ["Detalles Extraídos", "Comentarios", "Adjuntos"];

  // Filter out specific fields from the extracted details
  const extractedDetails = Object.entries(isRetrainingMode ? editedData : data)
    .filter(([key]) =>
        key !== "id" &&
        !key.toLowerCase().includes("url") &&
        key !== "correo" &&
        key !== "comentarios" &&
        key !== "resumen_caso" &&
        key !== "respuesta_sugerida" &&
        key !== "group_id" &&
        key !== "workflow_run_id"
    )
    .map(([key, currentValue]) => {
      const originalValue = data[key];
      const displayValue = formatValueForDisplay(currentValue);
      const isEdited = isRetrainingMode && editedData[key] !== originalValue;

      // No mostrar campos vacíos o nulos a menos que se esté editando
      if (!isEditing && (currentValue === null || currentValue === "" || currentValue === undefined)) {
        return null;
      }

      return (
        <div key={key} className="pb-2 border-b border-slate-200/50 dark:border-slate-700/40 last:border-b-0">
          <label htmlFor={`field-${key}`} className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-0.5">
            {getColumnLabel(key)}
          </label>
          {isRetrainingMode || isEditing ? (
            <div className="relative">
              <Input
                id={`field-${key}`}
                value={isEditing ? (editedData[key] ?? currentValue ?? "") : (currentValue ?? "")}
                onChange={(e) => handleFieldEdit(key, e.target.value)}
                className={`w-full text-sm rounded px-3 py-1.5 h-9 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                ${
                  isEdited || (isEditing && editedData[key] !== originalValue)
                    ? "border-orange-400 dark:border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-100 placeholder:text-orange-700/70"
                    : "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700/50 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                }`}
              />
              {(isEdited || (isEditing && editedData[key] !== originalValue)) && (
                <div className="absolute right-2.5 top-1/2 transform -translate-y-1/2" title="Campo modificado">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-800 dark:text-slate-100 break-words pt-1 pb-1.5 min-h-[36px] flex items-center">
              {displayValue}
            </p>
          )}
        </div>
      );
    }).filter(Boolean); // Filtrar elementos nulos si no se está editando

  const handleUseSuggestedResponse = () => {
    if (data?.respuesta_sugerida) {
      setResponseText(data.respuesta_sugerida);
      toast({
        title: "Respuesta sugerida aplicada",
        description: "El texto se ha copiado al área de respuesta.",
        duration: 2500,
      });
    }
  };

  const handleSendResponse = async () => {
    const rowId = data?.id;
    const runId = data?.workflow_run_id || "1";
    
    // Validar que haya un correo destinatario válido
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!recipientEmail.trim() || !emailRegex.test(recipientEmail)) {
      toast({
        title: "Correo inválido",
        description: "Por favor ingresa una dirección de correo electrónico válida.",
        variant: "destructive",
        duration: 3000,
        style: {
          background: "#f44336",
          color: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
        }
      });
      return;
    }

    if (!responseText.trim()) {
      toast({
        title: "Falta mensaje",
        description: "Asegúrate de escribir una respuesta.",
        variant: "destructive",
        duration: 3000,
        style: {
          background: "#f44336",
          color: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
        }
      });
      return;
    }

    setIsSendingResponse(true);

    try {
      // --- Start: Process Attachments ---
      const processedAttachments = await Promise.all(
        attachmentsToSend.map(file => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file); // Reads as base64 data URL
            reader.onload = () => {
              // Extract base64 content (remove prefix 'data:*/*;base64,')
              const base64Content = reader.result.split(',')[1];
              resolve({
                filename: file.name,
                base64: base64Content,
                mimeType: file.type || 'application/octet-stream' // Get MIME type or use default
              });
            };
            reader.onerror = error => reject(error);
          });
        })
      );

      const emailData = {
        toEmails: [recipientEmail], // Usar el correo editable
        subject: `Estado de su caso con ETB con incidencia numero ${data?.incidencia}`,
        message: responseText,
        attachments: processedAttachments, // Include processed attachments
      };

      await sendGenericEmail(emailData);

      // Actualizar estado solo si tenemos los IDs necesarios
      if (rowId && clientId && workflowId) {
        try {
          await updateExtractedField(clientId, workflowId, 'estado', 'respondido', rowId, runId);
          if (data) data.estado = 'respondido';
        } catch (updateError) {
          console.error("Error al actualizar el estado después de enviar el correo:", updateError);
          toast({
            title: "Correo enviado, pero hubo un problema al actualizar el estado.",
            variant: "warning",
            duration: 4000,
            
          });
        }
      } else {
        console.warn("Faltan datos (clientId, workflowId, rowId) para actualizar el estado.");
        // Informar al usuario que el correo se envió pero el estado no se actualizó
        toast({
          title: "Correo enviado, pero no se pudo actualizar el estado (faltan IDs).",
          variant: "warning",
          duration: 4000,
        });
      }

      toast({
        title: "Respuesta enviada",
        description: `Correo enviado a ${recipientEmail}`,
        duration: 3000,
      });

      // Clear attachments after successful send
      setAttachmentsToSend([]);

    } catch (error) {
      console.error("Error al enviar la respuesta:", error);
      toast({
        title: "Error al enviar",
        description: error.response?.data?.message || error.message || "No se pudo enviar la respuesta.",
        variant: "destructive",
        duration: 3000,
        style: {
          background: "#f44336",
          color: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
        }
      });
    } finally {
      setIsSendingResponse(false);
    }
  };

  const handleAttachFile = () => {
    fileInputRef.current?.click(); // Trigger click on the hidden file input
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    // Optional: Add validation for file size, type, or count here
    setAttachmentsToSend(prev => [...prev, ...files]);
    // Clear the input value so the same file can be selected again if removed
    if (event.target) {
      event.target.value = null;
    }
  };

  const handleRemoveAttachment = (fileToRemove) => {
    setAttachmentsToSend(prev => prev.filter(file => file !== fileToRemove));
  };

  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
          isFullscreen ? "bg-black" : "bg-black/60 backdrop-blur-sm"
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <style jsx global>{`
          .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(156, 163, 175, 0.5); border-radius: 20px; }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: rgba(156, 163, 175, 0.7); }
          .custom-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(156, 163, 175, 0.5) transparent; }
          .dark .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(71, 85, 105, 0.5); }
          .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: rgba(71, 85, 105, 0.7); }
          .dark .custom-scrollbar { scrollbar-color: rgba(71, 85, 105, 0.5) transparent; }
        `}</style>

        <motion.div
          initial={{ scale: 0.95, opacity: 0.8 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-white dark:bg-slate-900 shadow-2xl overflow-hidden flex flex-col rounded-2xl border border-slate-200/80 dark:border-slate-700/60"
          style={isFullscreen ? { width: '100vw', height: '100vh', margin: 0, borderRadius: 0 } : { width: '90vw', maxWidth: '1400px', height: '90vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hidden File Input */}
          <input
            type="file"
            multiple // Allow multiple files
            ref={fileInputRef}
            onChange={handleFileSelect} // Add this handler
            style={{ display: 'none' }} // Keep it hidden
            accept="*" // Or specify accepted types like ".pdf,.jpg,.png"
          />
          <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Detalles del Proceso
            </h3>
            <div className="flex items-center space-x-1.5">
              {isRetrainingMode && hasEdits && !isRetraining && !retrainingComplete && (
                <>
                  <Button
                    onClick={handleSaveChanges}
                    variant="outline"
                    size="sm"
                    className="text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" /> Guardar
                  </Button>
                  <Button
                    onClick={startRetraining}
                    size="sm"
                    className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm shadow-orange-500/30"
                  >
                    <BrainCircuit className="h-4 w-4 mr-1.5" /> Entrenar
                  </Button>
                  <Separator orientation="vertical" className="h-6 mx-1 dark:bg-slate-600" />
                </>
              )}

              <div className="group relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleRetrainingMode}
                  className={`rounded-full text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ${
                    isRetrainingMode
                      ? "bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"
                      : ""
                  }`}
                >
                  <BrainCircuit className="h-5 w-5" />
                </Button>
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg pointer-events-none">
                  {isRetrainingMode ? "Salir Modo Edición IA" : "Editar y Reentrenar IA"}
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                className="rounded-full text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                {isFullscreen ? (
                  <Minimize2 className="h-5 w-5" />
                ) : (
                  <Maximize2 className="h-5 w-5" />
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-900/30 dark:hover:text-red-400"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            <div className="w-[65%] flex flex-col border-r border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 dark:bg-slate-800/50">
                {filesToPreview.length > 1 && (
                  <div className="flex-shrink-0 border-b border-slate-200 dark:border-slate-700 px-3 pt-3 overflow-x-auto custom-scrollbar-thin">
                    <div className="flex space-x-1">
                      {filesToPreview.map((file, index) => (
                        <button
                          key={`${file.url}-${index}`}
                          onClick={() => setSelectedFileToPreview(file)}
                          className={`
                            px-3 py-2 text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center gap-1.5 rounded-t-lg mb-[-1px] border border-b-0 border-transparent
                            ${
                              selectedFileToPreview?.url === file.url && selectedFileToPreview?.type === file.type
                                ? "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 border-slate-200 dark:border-slate-700"
                                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                            }
                          `}
                        >
                          {getAttachmentIcon(file.type)}
                          {file.name || `Archivo ${index + 1}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex-1 overflow-auto custom-scrollbar p-4 sm:p-6">
                  {selectedFileToPreview ? (
                    <>
                      {selectedFileToPreview.type === "correo_content" ? (
                         <div className="whitespace-pre-wrap text-sm text-slate-800 dark:text-slate-200 leading-relaxed">
                           {highlightText(data?.correo || "Contenido no disponible.", searchText)}
                         </div>
                      ) : selectedFileToPreview.type === "email" ? (
                         <div className="p-4 sm:p-6 h-full overflow-y-auto custom-scrollbar">
                            {data?.senderEmail && <p><strong>De:</strong> {highlightText(data.senderEmail, searchText)}</p>}
                            {data?.subject && <p><strong>Asunto:</strong> {highlightText(data.subject, searchText)}</p>}
                            {data?.body && <div className="mt-2 whitespace-pre-wrap">{highlightText(data.body, searchText)}</div>}
                         </div>
                      ) : selectedFileToPreview.url?.toLowerCase().endsWith(".pdf") ? (
                        <iframe
                          src={selectedFileToPreview.url}
                          className="w-full h-full"
                          title="Documento PDF"
                          style={{ border: "none" }}
                        />
                      ) : selectedFileToPreview.url?.match(/\.(xls|xlsx|doc|docx|ppt|pptx)$/i) ? (
                        <iframe
                          src={`https://docs.google.com/gview?url=${encodeURIComponent(selectedFileToPreview.url)}&embedded=true`}
                          className="w-full h-full"
                          title="Documento Office"
                          style={{ border: "none" }}
                        />
                      ) : selectedFileToPreview.url?.match(/\.(jpeg|jpg|png|gif|bmp)$/i) ? (
                        <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                           <img
                             src={selectedFileToPreview.url}
                             alt={selectedFileToPreview.name || "Imagen"}
                             className="max-w-full max-h-full object-contain"
                           />
                         </div>
                      ) : selectedFileToPreview.url ? (
                         <div className="h-full flex flex-col items-center justify-center text-center p-8">
                           <div>
                             <FileText className="h-16 w-16 mx-auto text-slate-400 dark:text-slate-500 mb-4" />
                             <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
                               Vista previa no disponible
                             </p>
                             <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                               No se puede mostrar este tipo de archivo ({selectedFileToPreview.url.split('.').pop()}).
                             </p>
                             <Button size="sm" variant="outline" className="mt-4" asChild>
                                <a href={selectedFileToPreview.url} target="_blank" rel="noopener noreferrer">
                                   Descargar Archivo
                                </a>
                             </Button>
                           </div>
                         </div>
                      ) : (
                         <div className="h-full flex flex-col items-center justify-center text-center p-8">
                           <div>
                             <FileText className="h-16 w-16 mx-auto text-slate-400 dark:text-slate-500 mb-4" />
                             <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
                               Vista previa no disponible
                             </p>
                             <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                               No se puede mostrar este tipo de archivo ({selectedFileToPreview.url.split('.').pop()}).
                             </p>
                             <Button size="sm" variant="outline" className="mt-4" asChild>
                                <a href={selectedFileToPreview.url} target="_blank" rel="noopener noreferrer">
                                   Descargar Archivo
                                </a>
                             </Button>
                           </div>
                         </div>
                      )}
                    </>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8">
                      <FileText className="h-16 w-16 mx-auto text-slate-400 dark:text-slate-500 mb-4" />
                      <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
                        No hay documento seleccionado
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        {filesToPreview.length > 0 ? "Seleccione un archivo de las pestañas superiores." : "No se encontraron archivos para previsualizar."}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-shrink-0 border-t border-slate-200 dark:border-slate-700 p-4 sm:p-5 bg-white dark:bg-slate-900 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <label htmlFor="response-textarea" className="text-sm font-medium text-slate-800 dark:text-slate-200 block">
                      Respuesta al Cliente
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-slate-400" />
                    <Input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="Correo del destinatario"
                      className="text-xs w-64 h-7 py-1 px-2 text-slate-900 dark:text-slate-100 border-slate-300 dark:border-slate-600 rounded"
                    />
                  </div>
                </div>
                <Textarea
                  id="response-textarea"
                  ref={textareaRef}
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Escribe tu respuesta aquí..."
                  className="w-full min-h-[100px] max-h-[200px] text-sm custom-scrollbar bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 focus:outline-none transition duration-150 borderRadius-lg"
                  rows={4}
                />

                {/* Display selected attachments */}
                {attachmentsToSend.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-medium text-slate-600 dark:text-slate-400">Archivos adjuntos:</p>
                    <ul className="space-y-1.5">
                      {attachmentsToSend.map((file, index) => (
                        <li key={index} className="flex items-center justify-between text-sm bg-slate-100 dark:bg-slate-800 p-2 rounded">
                          <div className="flex items-center gap-2 overflow-hidden">
                            <Paperclip className="h-4 w-4 text-slate-500 flex-shrink-0" />
                            <span className="truncate text-slate-700 dark:text-slate-300" title={file.name}>
                              {file.name}
                            </span>
                            <span className="text-xs text-slate-500 flex-shrink-0">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemoveAttachment(file)}
                            className="p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title="Quitar adjunto"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-between items-center pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAttachFile}
                    className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded transition-all duration-150 focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                  >
                    <Paperclip className="h-4 w-4 mr-1.5" />
                    Adjuntar
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSendResponse}
                    disabled={!responseText.trim() || isSendingResponse}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 focus:ring-2 focus:ring-orange-500/30 focus:outline-none w-[190px] border border-orange-400 dark:border-orange-700 transform hover:-translate-y-0.5 active:translate-y-0"
                  >
                    {isSendingResponse ? (
                      <div className="flex items-center justify-center">
                        <div className="relative">
                          <div className="h-4 w-4 rounded border-2 border-transparent border-t-white border-r-white animate-spin"></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-2 w-2 bg-white rounded animate-pulse"></div>
                          </div>
                        </div>
                        <span className="ml-2">Enviando...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center group">
                        <Send className="h-4 w-4 mr-1.5 group-hover:animate-pulse" />
                        <span className="group-hover:tracking-wide transition-all duration-300">Enviar respuesta</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className="w-[35%] flex flex-col overflow-hidden bg-slate-50 dark:bg-slate-800/50">
              {isRetrainingMode ? (
                 <div className="flex-1 flex flex-col p-4 sm:p-6 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800/80 dark:to-slate-900 overflow-y-auto custom-scrollbar">
                   <AnimatePresence mode="wait">
                     {retrainingComplete ? (
                       <motion.div
                         key="complete"
                         initial={{ opacity: 0, y: 10 }}
                         animate={{ opacity: 1, y: 0 }}
                         exit={{ opacity: 0 }}
                         className="space-y-6 flex-1 flex flex-col"
                       >
                         <motion.div
                           initial={{ scale: 0.9 }}
                           animate={{ scale: 1 }}
                           transition={{ duration: 0.5 }}
                           className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/40 dark:to-green-800/30 rounded p-5 border border-green-200/80 dark:border-green-700/50 relative overflow-hidden shadow-lg shadow-green-500/10 dark:shadow-green-900/20"
                         >
                           <div className="absolute top-0 right-0 -mt-4 -mr-4">
                             <motion.div
                               initial={{ scale: 0, rotate: -45 }}
                               animate={{ scale: 1, rotate: 0 }}
                               transition={{ delay: 0.3, duration: 0.5 }}
                             >
                               <Sparkles className="h-10 w-10 text-green-500/50" />
                             </motion.div>
                           </div>

                           <div className="flex items-start space-x-4">
                             <div className="bg-white dark:bg-slate-800 rounded-full p-2.5 shadow-md flex-shrink-0">
                               <CheckCircle className="h-6 w-6 text-green-500" />
                             </div>

                             <div className="flex-1 min-w-0">
                               <h4 className="font-medium text-green-700 dark:text-green-400 text-lg mb-3">
                                 ¡Reentrenamiento completado!
                               </h4>

                               <div className="space-y-4">
                                 <div>
                                   <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                                     Precisión anterior
                                   </div>
                                   <div className="flex items-center">
                                     <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5 mr-2">
                                       <motion.div
                                         initial={{ width: 0 }}
                                         animate={{ width: `${accuracyBefore}%` }}
                                         transition={{ duration: 1, delay: 0.2 }}
                                         className="bg-slate-400 dark:bg-slate-500 h-2.5 rounded-full"
                                       />
                                     </div>
                                     <span className="font-medium text-slate-700 dark:text-slate-300 text-sm w-12 text-right">
                                       {accuracyBefore.toFixed(1)}%
                                     </span>
                                   </div>
                                 </div>

                                 <div>
                                   <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                                     Nueva precisión
                                   </div>
                                   <div className="flex items-center">
                                     <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5 mr-2">
                                       <motion.div
                                         initial={{ width: 0 }}
                                         animate={{ width: `${accuracyAfter}%` }}
                                         transition={{ duration: 1.5, delay: 0.5 }}
                                         className="bg-green-500 dark:bg-green-400 h-2.5 rounded-full"
                                       />
                                     </div>
                                     <span className="font-medium text-green-600 dark:text-green-400 text-sm w-12 text-right">
                                       {accuracyAfter.toFixed(1)}%
                                     </span>
                                   </div>
                                 </div>

                                 <div className="flex items-center justify-between pt-3 mt-2 border-t border-green-200/70 dark:border-green-800/40">
                                   <span className="text-slate-700 dark:text-slate-300 text-sm font-medium flex items-center">
                                     <TrendingUp className="h-4 w-4 mr-1.5 text-green-500" />
                                     Mejora:
                                   </span>
                                   <span className="font-bold text-green-600 dark:text-green-400 text-sm">
                                     +{(accuracyAfter - accuracyBefore).toFixed(1)}%
                                   </span>
                                 </div>
                               </div>
                             </div>
                           </div>
                         </motion.div>

                         <div className="bg-white dark:bg-slate-800 rounded p-4 border border-slate-200/70 dark:border-slate-700/50 shadow-md">
                           <h4 className="font-medium text-slate-800 dark:text-slate-200 text-sm mb-1.5 flex items-center">
                             <Target className="h-4 w-4 mr-1.5 text-orange-500" />
                             Motivo del reentrenamiento
                           </h4>
                           <p className="text-slate-600 dark:text-slate-300 text-sm italic">
                             "{retrainingReason || "No especificado"}"
                           </p>
                         </div>

                         <div className="mt-auto pt-4 flex justify-end">
                           <Button
                             onClick={toggleRetrainingMode}
                             variant="outline"
                             className="rounded text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors duration-150"
                           >
                             Volver a los datos
                           </Button>
                         </div>
                       </motion.div>
                     ) : (
                       <motion.div
                         key="training"
                         initial={{ opacity: 0 }}
                         animate={{ opacity: 1 }}
                         exit={{ opacity: 0 }}
                         className="flex-1 flex flex-col items-center justify-center text-center p-6"
                       >
                         <div className="relative mb-6">
                           <div className="w-20 h-20 rounded-full border-4 border-orange-100 dark:border-orange-900/30 flex items-center justify-center shadow-md shadow-orange-500/10">
                             <motion.div
                               animate={{ rotate: 360 }}
                               transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                               className="absolute inset-0"
                             >
                               <svg className="w-full h-full" viewBox="0 0 100 100">
                                 <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" strokeLinecap="round" strokeDasharray="62.83 188.5" fill="transparent" className="text-orange-500" />
                               </svg>
                             </motion.div>
                             <BrainCircuit className="h-9 w-9 text-orange-500" />
                           </div>
                           <motion.div
                             initial={{ scale: 0 }}
                             animate={{ scale: [0, 1.2, 1] }}
                             transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 4 }}
                             className="absolute top-0 right-0 -mt-2 -mr-2"
                           >
                             <Sparkles className="h-5 w-5 text-orange-400" />
                           </motion.div>
                         </div>

                         <h4 className="text-lg font-medium text-slate-800 dark:text-slate-200 mb-1">
                           Reentrenando IA...
                         </h4>
                         <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 min-h-[20px]">
                           {retrainingStage}
                         </p>

                         <div className="w-full max-w-xs mb-4">
                           <div className="h-2 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden shadow-inner">
                             <motion.div
                               initial={{ width: 0 }}
                               animate={{ width: `${retrainingProgress}%` }}
                               className="h-full bg-gradient-to-r from-orange-400 to-orange-600 rounded-full transition-width duration-150 ease-linear"
                             />
                           </div>
                           <div className="flex justify-between mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                             <span>{retrainingProgress}%</span>
                           </div>
                         </div>

                         <div className="text-center text-slate-400 dark:text-slate-500 text-xs mt-4 max-w-xs">
                           <p>Este proceso puede tardar unos momentos.</p>
                           <p>Mejorando el modelo según tus indicaciones.</p>
                         </div>
                       </motion.div>
                     )}
                   </AnimatePresence>
                 </div>
               ) : (
                 <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6 space-y-5">
                   <Tab tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

                   {activeTab === "Detalles Extraídos" && (
                     <div className="space-y-4">
                       {data?.resumen_caso && (
                         <details className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50 rounded shadow-sm overflow-hidden">
                           <summary className="text-sm font-semibold text-blue-700 dark:text-blue-300 p-3 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/50 flex items-center">
                             <Sparkles className="h-4 w-4 mr-2 flex-shrink-0 text-blue-500" />
                             Resumen IA
                             <ChevronDown className="h-4 w-4 ml-auto transition-transform" />
                           </summary>
                           <div className="p-3 pt-0 text-sm text-slate-700 dark:text-slate-300 leading-relaxed border-t border-blue-200 dark:border-blue-800/50">
                             {highlightText(data.resumen_caso, searchText)}
                           </div>
                         </details>
                       )}

                       {data?.accion_sugerida && (
                         <details className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800/50 rounded shadow-sm overflow-hidden">
                           <summary className="text-sm font-semibold text-purple-700 dark:text-purple-300 p-3 cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/50 flex items-center">
                             <ListChecks className="h-4 w-4 mr-2 flex-shrink-0 text-purple-500" />
                             Acciones Sugeridas
                             <ChevronDown className="h-4 w-4 ml-auto transition-transform" />
                           </summary>
                           <div className="p-3 pt-0 text-sm text-slate-700 dark:text-slate-300 leading-relaxed border-t border-purple-200 dark:border-purple-800/50">
                             <div className="space-y-2">
                               {formatAccionesSugeridas(data.accion_sugerida).split('\n\n').map((accion, index) => (
                                 <div key={index} className="flex items-start">
                                   <div className="flex-1">
                                     {highlightText(accion, searchText)}
                                   </div>
                                 </div>
                               ))}
                             </div>
                           </div>
                         </details>
                       )}

                       {data?.respuesta_sugerida && (
                         <details className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800/50 rounded shadow-sm overflow-hidden">
                           <summary className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 p-3 cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/50 flex items-center">
                             <MessageSquare className="h-4 w-4 mr-2 flex-shrink-0 text-emerald-500" />
                             Respuesta Sugerida
                             <ChevronDown className="h-4 w-4 ml-auto transition-transform" />
                           </summary>
                           <div className="p-3 pt-0 text-sm text-slate-700 dark:text-slate-300 leading-relaxed border-t border-emerald-200 dark:border-emerald-800/50">
                             <div className="flex justify-between items-center mb-2">
                               <span className="text-xs text-emerald-600 dark:text-emerald-400">Generada por IA</span>
                               <Button
                                 variant="ghost"
                                 size="sm"
                                 onClick={handleUseSuggestedResponse}
                                 className="text-xs h-7 px-2 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
                               >
                                 <Copy className="h-3.5 w-3.5 mr-1" />
                                 Usar respuesta
                               </Button>
                             </div>
                             <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                               {highlightText(data.respuesta_sugerida, searchText)}
                             </p>
                           </div>
                         </details>
                       )}

                       <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm p-4">
                         <div className="space-y-3">
                           {extractedDetails}
                         </div>
                       </div>
                     </div>
                   )}

                   {activeTab === "Comentarios" && (
                     <div className="space-y-4">
                       <div className="flex items-center justify-between">
                         <h4 className="text-base font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                           <MessageSquare className="h-5 w-5 text-orange-500" /> 
                           Comentarios Internos
                         </h4>
                         <span className="text-xs text-slate-500 dark:text-slate-400">
                           {comments.length} comentarios
                         </span>
                       </div>
                       <div className="space-y-3 max-h-72 overflow-y-auto custom-scrollbar pr-2 -mr-2">
                         {comments.length > 0 ? (
                           comments.map((comment) => (
                             <div key={comment.id} className="p-3.5 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 shadow-sm hover:border-orange-500/30 transition-colors duration-150">
                               <div className="flex items-center justify-between mb-2">
                                 <div className="flex items-center gap-2">
                                   <div className="h-6 w-6 rounded-full bg-orange-500/10 flex items-center justify-center">
                                     <User className="h-3.5 w-3.5 text-orange-500" />
                                   </div>
                                   <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                     {comment.clientName}
                                   </span>
                                 </div>
                                 <span className="text-xs text-slate-500 dark:text-slate-400">
                                   {new Date(comment.date).toLocaleString()}
                                 </span>
                               </div>
                               <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                 {comment.message}
                               </p>
                             </div>
                           ))
                         ) : (
                           <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-6 italic">
                             No hay comentarios.
                           </p>
                         )}
                       </div>
                       <div className="relative pt-2">
                         <Textarea
                           ref={commentTextareaRef}
                           value={newComment}
                           onChange={(e) => setNewComment(e.target.value)}
                           placeholder="Añadir un comentario interno..."
                           className="w-full p-3 pr-12 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 dark:bg-slate-800 resize-none overflow-hidden text-sm transition-all duration-150 focus:outline-none"
                           rows={1}
                         />
                         <Button
                           size="icon"
                           variant="ghost"
                           className="absolute right-2 bottom-2 h-8 w-8 rounded text-slate-500 hover:text-orange-500 dark:hover:text-orange-400 focus:text-orange-500 dark:focus:text-orange-400 disabled:text-slate-400 disabled:bg-transparent transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                           onClick={() => handleUpdateField("comentarios", newComment)}
                           disabled={!newComment.trim()}
                           title="Guardar Comentario"
                         >
                           <Send className="h-4 w-4" />
                         </Button>
                       </div>
                     </div>
                   )}

                   {activeTab === "Adjuntos" && (
                     <div className="space-y-3">
                       <h4 className="text-base font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2 mb-3">
                         <Paperclip className="h-5 w-5 text-green-500" /> Adjuntos
                       </h4>
                       <div className="space-y-2">
                         {mockAttachments.length > 0 ? (
                           mockAttachments.map((att) => (
                             <div
                               key={att.id}
                               className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700 shadow-sm"
                             >
                               <div className="flex items-center space-x-3 overflow-hidden mr-2">
                                 <div className="flex-shrink-0 text-slate-500">
                                   {getAttachmentIcon(att.type)}
                                 </div>
                                 <div className="overflow-hidden">
                                   <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate" title={att.name}>
                                     {att.name}
                                   </p>
                                   <p className="text-xs text-slate-500 dark:text-slate-400">
                                     {att.size} - {att.date}
                                   </p>
                                 </div>
                               </div>
                               <div className="flex space-x-1 flex-shrink-0">
                                 <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-orange-600 dark:hover:text-orange-400 focus:text-orange-600 dark:focus:text-orange-400 rounded transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-orange-500/50" title="Ver">
                                   <Eye className="h-4 w-4" />
                                 </Button>
                                 <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-orange-600 dark:hover:text-orange-400 focus:text-orange-600 dark:focus:text-orange-400 rounded transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-orange-500/50" title="Descargar" asChild>
                                   <a href={att.url} download={att.name}>
                                     <Download className="h-4 w-4" />
                                   </a>
                                 </Button>
                               </div>
                             </div>
                           ))
                         ) : (
                           <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-6 italic">
                             No hay adjuntos.
                           </p>
                         )}
                       </div>
                       <Button
                         variant="outline"
                         size="sm"
                         className="w-full mt-3 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded transition-colors duration-150 focus:ring-2 focus:ring-offset-1 focus:ring-orange-500/50 focus:outline-none"
                       >
                         <Paperclip className="h-4 w-4 mr-2" /> Subir Adjunto
                       </Button>
                     </div>
                   )}

                 </div>
               )}

            </div>

          </div>

        </motion.div>
      </motion.div>

      {showReassignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="bg-white dark:bg-slate-800 rounded shadow-xl p-6 w-full max-w-md border border-slate-200 dark:border-slate-700"
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-5">
              Reasignar a grupo
            </h3>
            <div className="space-y-2 mb-6 max-h-60 overflow-y-auto custom-scrollbar pr-1">
              {availableGroups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => handleReassign(group.id, group.name)}
                  disabled={isReassigning}
                  className={`w-full text-left px-4 py-2.5 rounded transition-colors duration-150 text-sm relative
                    ${isReassigning 
                      ? 'bg-slate-100 dark:bg-slate-700/50 cursor-not-allowed opacity-50' 
                      : 'hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-200'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{group.name}</span>
                    {isReassigning && (
                      <Loader2 className="h-4 w-4 text-orange-500 animate-spin ml-2" />
                    )}
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReassignModal(false)}
                className="rounded text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors duration-150"
              >
                Cancelar
              </Button>
            </div>
          </motion.div>
        </div>
      )}

    </AnimatePresence>
  );
}

